import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { useDebouncedValue } from "@packages/core-client/hooks/useDebouncedValue.ts";
import { useFeatureFlag } from "@squide/firefly";
import { useQuery } from "@tanstack/react-query";
import { useQueryStates } from "nuqs";
import { useCallback } from "react";
import { useSegmentsListClient } from "../../../api/queries/segments.ts";
import { SEGMENTS_SEARCH_DEBOUNCE_MS, segmentsQueryStateOptions, segmentsSearchQuerySchema } from "./segmentsSearch.ts";

export function useSegmentTabCounts() {
    const { queryUtils } = useSegmentsListClient();
    const session = useSession();
    const organizationId = session?.organizationId ?? "";
    const isSharedSegmentsEnabled = useFeatureFlag("enable-shared-segments", false);

    const [{ query, tags }] = useQueryStates(segmentsSearchQuerySchema, segmentsQueryStateOptions);
    const debouncedQuery = useDebouncedValue(query, SEGMENTS_SEARCH_DEBOUNCE_MS);
    const trimmedQuery = debouncedQuery.trim() || undefined;
    const selectedTags = tags.length > 0 ? tags : undefined;

    const { data: mine } = useQuery(createQueryOptions(queryUtils.countSegments.queryOptions({ input: { orgId: organizationId, scope: "mine", q: trimmedQuery, tags: selectedTags } }), { throwOnError: false }));
    const { data: sharedWithMe } = useQuery(createQueryOptions(queryUtils.countSegments.queryOptions({ input: { orgId: organizationId, scope: "shared-with-me", q: trimmedQuery, tags: selectedTags } }), { throwOnError: false, enabled: isSharedSegmentsEnabled }));

    const mineCount = mine?.totalCount;
    const sharedWithMeCount = sharedWithMe?.totalCount;

    return useCallback((tabId: string) => (tabId === "mine" ? mineCount : tabId === "shared-with-me" ? sharedWithMeCount : undefined), [mineCount, sharedWithMeCount]);
}
